import type { TourPlacement } from "./types";

// ── Portal & anchoring ──────────────────────────────────

export const TOUR_PORTAL_ID = "tourGuideTarget";
export const TOUR_ANCHOR_PREFIX = "--tour-";

export const toAnchorVar = (name: string): string =>
  `${TOUR_ANCHOR_PREFIX}${name}`;

// ── Z-index layers ──────────────────────────────────────

export const TOUR_Z_BACKDROP = 9000;
export const TOUR_Z_HIGHLIGHT = 9001;
export const TOUR_Z_TOOLTIP = 9002;

// ── Timing ──────────────────────────────────────────────

export const STEP_TRANSITION_DELAY_MS = 200;

// ── Layout: highlight ring ──────────────────────────────

export const HIGHLIGHT_RING_INSET_PX = -6;
export const HIGHLIGHT_RING_RADIUS_OFFSET_PX = 5;
export const HIGHLIGHT_RING_DEFAULT_RADIUS = "12px";
export const OUTER_BACKDROP_MAX_SIZE_PX = 400;

// ── Layout: tooltip ─────────────────────────────────────

export const TOOLTIP_OFFSET_PX = 16;
export const TOOLTIP_MAX_WIDTH_PX = 327;
export const TOOLTIP_MIN_WIDTH_PX = 200;
export const VIEWPORT_SAFE_MARGIN_PX = 16;

// ── Layout: arrow ───────────────────────────────────────

export const ARROW_WIDTH = 16;
export const ARROW_HEIGHT = 8;
export const ARROW_OVERLAP_PX = 1;
export const ARROW_GAP_PX = TOOLTIP_OFFSET_PX - ARROW_HEIGHT + ARROW_OVERLAP_PX;
export const ARROW_SIDE_GAP_PX = ARROW_GAP_PX / 2;

// ── Animation transitions ───────────────────────────────

export const SPRING_ENTER = {
  type: "spring" as const,
  damping: 22,
  stiffness: 340,
  mass: 0.8
};

export const QUICK_EXIT = {
  duration: 0.18,
  ease: [0.4, 0, 1, 1] as const
};

export const BACKDROP_ENTER_TRANSITION = {
  duration: 0.3,
  ease: [0.25, 0.1, 0.25, 1] as const
};

export const BACKDROP_EXIT_TRANSITION = {
  duration: 0.25,
  ease: [0.25, 0.1, 0.25, 1] as const
};

// ── Placement-dependent slide offsets ───────────────────

const SLIDE_ENTER_PX = 12;
const SLIDE_EXIT_PX = 6;

export const SLIDE_ENTER: Record<TourPlacement, { x?: number; y?: number }> = {
  bottom: { y: SLIDE_ENTER_PX },
  top: { y: -SLIDE_ENTER_PX },
  left: { x: -SLIDE_ENTER_PX },
  right: { x: SLIDE_ENTER_PX }
};

export const SLIDE_EXIT: Record<TourPlacement, { x?: number; y?: number }> = {
  bottom: { y: SLIDE_EXIT_PX },
  top: { y: -SLIDE_EXIT_PX },
  left: { x: -SLIDE_EXIT_PX },
  right: { x: SLIDE_EXIT_PX }
};

export const ARROW_ROTATION: Record<TourPlacement, number> = {
  bottom: 0,
  top: 180,
  left: 90,
  right: -90
};

// ── Highlight ring animation presets ────────────────────

export const RING_INITIAL = { opacity: 0, scale: 0.88 } as const;
export const RING_ENTER = { opacity: 1, scale: 1, transition: SPRING_ENTER };
export const RING_EXIT = { opacity: 0, scale: 0.95, transition: QUICK_EXIT };
